"use client"

import { addToCart } from "@lib/data/cart"
import { useIntersection } from "@lib/hooks/use-in-view"
import { HttpTypes } from "@medusajs/types"
import Divider from "@modules/common/components/divider"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { isEqual } from "lodash"
import { useParams, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import ProductPrice from "../product-price"
import MobileActions from "./mobile-actions"
import { getProductStockQuantity } from "@modules/products/util/stock"
import { useT } from "@lib/i18n/use-t"

// Locale narrowing kept for ProductPrice + MobileActions consumers
function loc(cc: string): "ua" | "ru" | "en" {
  const c = (cc || "").toLowerCase()
  if (c.includes("ru")) return "ru"
  if (c === "us" || c === "gb" || c.includes("en")) return "en"
  return "ua"
}

// ─────────────────────────────────────────────────────────
type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt) => {
    if (varopt.option_id) acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

export default function ProductActions({
  product,
  disabled,
}: ProductActionsProps) {
  const searchParams = useSearchParams()

  const [options, setOptions] = useState<Record<string, string | undefined>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)

  const countryCode = useParams().countryCode as string
  const l = loc(countryCode)
  const t = useT()

  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
    }
  }, [product.variants])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return
    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => ({ ...prev, [optionId]: value }))
  }

  const isValidVariant = useMemo(() => {
    return product.variants?.some((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = isValidVariant ? selectedVariant?.id : null
    if (params.get("v_id") === value) return
    if (value) {
      params.set("v_id", value)
    } else {
      params.delete("v_id")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, isValidVariant])

  const inStock = useMemo(() => {
    if (!selectedVariant) return false
    if (selectedVariant.allow_backorder) return true
    // When inventory isn't managed (MVP default for marketplace imports),
    // fall back to metadata-based stock matching what the badge displays.
    // Keeping a single source of truth between button and badge prevents
    // the "shows 8 in stock but button disabled" mismatch.
    if (!selectedVariant.manage_inventory) {
      return getProductStockQuantity(product) > 0
    }
    return (selectedVariant.inventory_quantity || 0) > 0
  }, [selectedVariant, product])

  const actionsRef = useRef<HTMLDivElement>(null)
  const inView = useIntersection(actionsRef, "0px")

  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return
    setIsAdding(true)
    await addToCart({ variantId: selectedVariant.id, quantity, countryCode })
    setIsAdding(false)
  }

  return (
    <>
      <div className="flex flex-col gap-y-2" ref={actionsRef}>
        {(product.variants?.length ?? 0) > 1 && (
          <div className="flex flex-col gap-y-4 mb-2">
            {(product.options || []).map((option) => (
              <div key={option.id}>
                <OptionSelect
                  option={option}
                  current={options[option.id]}
                  updateOption={setOptionValue}
                  title={option.title ?? ""}
                  data-testid="product-options"
                  disabled={!!disabled || isAdding}
                />
              </div>
            ))}
            <Divider />
          </div>
        )}

        {/* Sale-only price (only shown when on discount; primary price lives in PriceCard above) */}
        {(product.variants?.length ?? 0) > 1 && (
          <ProductPrice product={product} variant={selectedVariant} locale={l} />
        )}

        {/* Quantity input */}
        <div className="mt-1 flex items-center gap-3">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t("pdpQty")}
          </span>
          <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 dark:border-white/10">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={!!disabled || isAdding || quantity <= 1}
              aria-label="-"
              className="px-3 py-2 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-white/10"
            >
              −
            </button>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              disabled={!!disabled || isAdding}
              className="w-14 border-x border-slate-200 bg-transparent py-2 text-center text-sm font-medium text-slate-900 [appearance:textfield] focus:outline-none disabled:opacity-50 dark:border-white/10 dark:text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
              type="button"
              onClick={() => setQuantity((q) => q + 1)}
              disabled={!!disabled || isAdding}
              aria-label="+"
              className="px-3 py-2 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-white/10"
            >
              +
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddToCart}
          disabled={!inStock || !selectedVariant || !!disabled || isAdding || !isValidVariant}
          data-testid="add-product-button"
          className="mt-2 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-light px-6 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:opacity-100 disabled:hover:opacity-100 dark:bg-brand-dark dark:disabled:bg-white/10 dark:disabled:text-slate-500"
        >
          {isAdding && (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-4 w-4 animate-spin"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 11-9-9" strokeLinecap="round" />
            </svg>
          )}
          {!selectedVariant && !options
            ? t("pdpSelectVariant")
            : !inStock || !isValidVariant
            ? t("pdpOutOfStock")
            : t("pdpAddToCart")}
        </button>

        <MobileActions
          product={product}
          variant={selectedVariant}
          options={options}
          updateOptions={setOptionValue}
          inStock={inStock}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          show={!inView}
          optionsDisabled={!!disabled || isAdding}
          quantity={quantity}
          locale={l}
        />
      </div>
    </>
  )
}
